/**
 * bank-analyser (StatementFlow) client — CONFIG-GATED, and deliberately isolated
 * from the crypto `api` axios instance. It talks to a DIFFERENT origin
 * (bank-analyser) using its X-API-Key model, so the fusion demo can trigger a
 * real statement upload + read results + deep-link, without any crypto backend
 * dependency (the backend fiat services are separate WIP).
 *
 * Enable by setting, at build time:
 *   VITE_STATEMENTFLOW_URL   = https://<bank-analyser-host>          (required)
 *   VITE_STATEMENTFLOW_KEY   = <X-API-Key>                            (optional; dev mode is open)
 *   VITE_STATEMENTFLOW_CASE_ID = <existing case uuid>                 (optional; else a demo case is created)
 *
 * bank-analyser must allowlist this frontend's origin in its ALLOWED_ORIGINS
 * (CORS) for browser-direct calls. The X-API-Key is visible in the bundle —
 * acceptable for a gated demo; production routes this server-to-server through
 * the crypto backend once the fiat services land on main.
 */
const ENV = import.meta.env as unknown as Record<string, string | undefined>;
const RAW_BASE = ENV.VITE_STATEMENTFLOW_URL;
const BASE = RAW_BASE ? RAW_BASE.replace(/\/$/, '') : undefined;
const KEY = ENV.VITE_STATEMENTFLOW_KEY;
const CONFIGURED_CASE = ENV.VITE_STATEMENTFLOW_CASE_ID;
// The deep-link ("Open full ledger") must target the bank-analyser *frontend*
// (a browsable SPA), NOT the API host in VITE_STATEMENTFLOW_URL — the API is
// X-API-Key-gated and renders nothing in a browser. Falls back to BASE for
// deployments where the UI is served from the same host as the API.
const RAW_UI = ENV.VITE_STATEMENTFLOW_UI_URL;
const UI_BASE = (RAW_UI ? RAW_UI.replace(/\/$/, '') : undefined) || BASE;

export interface SFAccountKyc {
  id?: string;
  bank?: string;
  account_type?: string;
  account_number?: string;
  holder_name?: string;
  ifsc?: string | null;
  branch?: string | null;
  bank_location?: string | null;
  bank_phone?: string | null;
  bank_email?: string | null;
}

export interface SFStatement {
  id: string;
  account_id: string;
  source_file_name?: string;
  extracted_txn_count?: number;
  requires_review?: boolean;
  narrative_summary?: string;
  risk_level?: string;
  anomalies?: Array<{ type: string; severity: string; description: string }>;
}

export interface SFTxn {
  id: string;
  account_id: string;
  txn_date: string;
  amount: number;
  direction: 'Dr' | 'Cr';
  running_balance?: number;
  raw_description?: string;
  entities?: Record<string, { value: string; source?: string; confidence?: number }>;
  tags?: string[];
}

export interface SFUploadResult {
  statement: SFStatement;
  account?: SFAccountKyc;
  transaction_count: number;
  latency_ms?: number;
}

function headers(json = true): Record<string, string> {
  const h: Record<string, string> = {};
  if (json) h['Content-Type'] = 'application/json';
  if (KEY) h['X-API-Key'] = KEY;
  return h;
}

function detailToString(detail: unknown): string | undefined {
  if (detail == null) return undefined;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    const o = detail as Record<string, unknown>;
    if (typeof o.message === 'string') return o.message;
    if (typeof o.detail === 'string') return o.detail;
    try { return JSON.stringify(detail); } catch { /* ignore */ }
  }
  return String(detail);
}

/** bank-analyser signals encrypted PDFs this way — keep in sync with its upload handler. */
export function isPdfPasswordRequiredError(message: string | undefined | null): boolean {
  if (!message) return false;
  return /password-protected|password[`'"]?\s*form\s*field|requires?\s+(a\s+)?password|incorrect\s+password|wrong\s+password|invalid\s+password|decrypt/i.test(message);
}

/**
 * Lightweight client-side check: encrypted PDFs declare an `/Encrypt` dictionary.
 * Not a substitute for server open — wrong/missing password still fails on upload —
 * but lets the UI prompt before the first round-trip when the marker is present.
 */
export async function pdfLooksPasswordProtected(file: File): Promise<boolean> {
  const name = file.name.toLowerCase();
  const isPdf = name.endsWith('.pdf') || file.type === 'application/pdf';
  if (!isPdf) return false;
  try {
    const slice = file.slice(0, Math.min(file.size, 512 * 1024));
    const buf = await slice.arrayBuffer();
    const text = new TextDecoder('latin1').decode(buf);
    // Dictionary key form: /Encrypt << … >> or /Encrypt N (object ref)
    return /\/Encrypt[\s\/<\d]/.test(text);
  } catch {
    return false;
  }
}

async function sf<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE) throw new Error('bank-analyser is not configured (set VITE_STATEMENTFLOW_URL).');
  const res = await fetch(`${BASE}${path}`, init);
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = await res.json();
      detail = detailToString(j.detail?.message) || detailToString(j.detail) || detailToString(j.message) || detail;
    } catch { /* non-json */ }
    const err = new Error(typeof detail === 'string' ? detail : String(detail)) as Error & {
      status?: number;
      passwordRequired?: boolean;
    };
    err.status = res.status;
    err.passwordRequired = isPdfPasswordRequiredError(err.message);
    throw err;
  }
  return res.json() as Promise<T>;
}

function statementReady(st: SFStatement | undefined, txnCount?: number): boolean {
  if (!st) return false;
  if ((txnCount ?? 0) > 0) return true;
  if ((st.extracted_txn_count ?? 0) > 0) return true;
  if (st.requires_review) return true;
  return false;
}

export const statementFlow = {
  isConfigured: (): boolean => !!BASE,
  hasKey: (): boolean => !!KEY,
  baseUrl: (): string | undefined => BASE,

  /** Deep-link into the bank-analyser FRONTEND focused on a case (and optionally
   *  a statement). Uses the UI host, not the API host. */
  deepLink(caseId: string, statementId?: string): string | undefined {
    if (!UI_BASE) return undefined;
    return `${UI_BASE}/cases/${caseId}${statementId ? `?statement=${statementId}` : ''}`;
  },

  async ensureCase(): Promise<string> {
    if (CONFIGURED_CASE) return CONFIGURED_CASE;
    const r = await sf<{ id: string }>('/api/cases', {
      method: 'POST', headers: headers(),
      body: JSON.stringify({ fir_number: 'FUSION-DEMO', title: 'Fusion demo import', officer_name: 'Fusion Workbench' }),
    });
    return r.id;
  },

  async ensurePerson(caseId: string, name: string): Promise<string> {
    const r = await sf<{ id: string }>(`/api/cases/${caseId}/persons`, {
      method: 'POST', headers: headers(), body: JSON.stringify({ name }),
    });
    return r.id;
  },

  /** Upload a statement file; retries once with overwrite on a duplicate (409).
   *  Upload is synchronous on StatementFlow — the response already includes the
   *  parsed statement + header KYC account block. */
  async uploadStatement(
    caseId: string, personId: string, file: File,
    opts?: { bank?: string; password?: string; overwrite?: boolean },
  ): Promise<SFUploadResult> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('person_id', personId);
    if (opts?.bank) fd.append('bank', opts.bank);
    if (opts?.password) fd.append('password', opts.password);
    if (opts?.overwrite) fd.append('overwrite', 'true');
    try {
      return await sf<SFUploadResult>(`/api/cases/${caseId}/statements`, {
        method: 'POST', headers: headers(false), body: fd,
      });
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status === 409 && !opts?.overwrite) {
        return this.uploadStatement(caseId, personId, file, { ...opts, overwrite: true });
      }
      throw err;
    }
  },

  getStatement: (statementId: string) => sf<SFStatement>(`/api/statements/${statementId}`, { headers: headers() }),

  /** Prefer the KYC block returned on upload; fall back to case detail accounts. */
  async getAccount(caseId: string, accountId: string): Promise<SFAccountKyc | null> {
    try {
      const detail = await sf<{ accounts?: SFAccountKyc[] }>(`/api/cases/${caseId}`, { headers: headers() });
      const hit = (detail.accounts || []).find((a) => a.id === accountId);
      return hit || null;
    } catch {
      return null;
    }
  },

  async getTransactions(caseId: string, accountId?: string, limit = 500): Promise<SFTxn[]> {
    const q = new URLSearchParams({ limit: String(limit) });
    if (accountId) q.set('account_id', accountId);
    const r = await sf<{ items: SFTxn[] }>(`/api/cases/${caseId}/transactions?${q.toString()}`, { headers: headers() });
    return r.items || [];
  },

  /**
   * Resolve the parsed statement without wasting round-trips.
   * StatementFlow upload is request-scoped (no job queue) — when the upload
   * response already has rows / review flag, return it immediately.
   */
  async waitForParse(
    statementId: string,
    opts: {
      tries?: number;
      intervalMs?: number;
      seed?: SFStatement;
      transactionCount?: number;
    } = {},
  ): Promise<SFStatement> {
    const { tries = 8, intervalMs = 800, seed, transactionCount } = opts;
    if (seed && statementReady(seed, transactionCount)) return seed;

    let last: SFStatement | undefined = seed;
    for (let i = 0; i < tries; i++) {
      last = await this.getStatement(statementId);
      if (statementReady(last)) return last;
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return last as SFStatement;
  },
};
